import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Define fetchProfile with useCallback to stabilize its identity
  const fetchProfile = useCallback(async (userId, sessionUser = null) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      // Use passed sessionUser or current user from session state as fallback
      // Note: In auth change events, it's safer to rely on the passed parameter
      const email = sessionUser?.email;

      if (data) {
        setUser({
          ...data,
          email,
          id: userId,
          app_metadata: sessionUser?.app_metadata,
          identities: sessionUser?.identities
        });
      } else {
        // Fallback if no profile row exists yet
        if (sessionUser) {
          setUser({
            id: userId,
            email: sessionUser.email,
            ...sessionUser.user_metadata // Try to get name from metadata
          });
        }
      }
    } catch (error) {
      console.error("Error fetching profile context", error);
      // Ensure we at least set the basic auth user if profile fetch failed
      if (sessionUser) {
        setUser({
          id: userId,
          email: sessionUser.email,
          ...sessionUser.user_metadata
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // 1. Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      if (session?.user) {
        fetchProfile(session.user.id, session.user);
      } else {
        setLoading(false);
      }
    });

    // 2. Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setSession(session);

      if (session?.user) {
        // FIX: Set loading to true to prevent ProtectedRoute from redirecting 
        // while profile is being fetched
        if (!user) {
          setLoading(true);
        }
        // Pass the user object directly to avoid stale state issues
        fetchProfile(session.user.id, session.user);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Method to manually refresh profile (e.g., after update)
  const refreshProfile = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await fetchProfile(session.user.id, session.user);
    }
  };

  const logout = async () => {
    try {
      // 1. Optimistic Logout: Clear state immediately for UI response
      setSession(null);
      setUser(null);

      // 2. Clear persistence
      localStorage.removeItem('finassure_user');
      sessionStorage.clear(); // Clear session storage as well

      // 3. Perform server-side signout
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout error", error);
      // Even if server error, UI should remain logged out
      setSession(null);
      setUser(null);
    }
  };

  const value = {
    user,
    session,
    loading,
    refreshProfile,
    logout,
    isAuthenticated: !!session,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
